import { useCallback, useState, useEffect } from 'react'
import { apiService } from '@/services/api'
import type { User } from '@/types'

/**
 * Hook personalizado para gerenciar o usuário atual
 * Provê funções para carregar o usuário atual e o estado de carregamento
 */
export function useCurrentUser(initialUser: User | null = null) {
  const [user, setUser] = useState<User | null>(initialUser)
  const [isLoading, setIsLoading] = useState(false)
  
  const loadUser = useCallback(async () => {
    setIsLoading(true)
    try {
      const userData = await apiService.getCurrentUser()
      setUser(userData)
      return userData
    } catch (error) {
      console.error('Erro ao carregar usuário:', error)
      return null
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Carregar usuário automaticamente na inicialização (se não houver usuário inicial)
  useEffect(() => {
    if (!initialUser) {
      loadUser()
    }
  }, [initialUser, loadUser])
  
  // Adicionar listeners para atualização automática do usuário
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadUser()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('focus', loadUser)
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('focus', loadUser)
    }
  }, [loadUser])

  return { user, isLoading, loadUser }
}